import { useMutation, useQueryClient } from '@tanstack/react-query';
import { mergeWith } from 'lodash';

import { type ShareUrlGeneratedObjectType, type ShareUrlUpdatedSetting, useTrackShareUrlUpdated } from '@air/analytics';
import { ShortUrl } from '@air/api';
import { type Board, type Clip, type Form, type ShortUrlResponse } from '@air/api/types';
import { reportErrorToBugsnag } from '@air/utils-error';

import {
  getShortUrlUpdatePresentationContent,
  type ShortUrlMutationPresentationContent,
} from '~/swr-hooks/shortUrl/getShortUrlUpdatePresentationContent';
import {
  getShortUrlUpdatePresentationPages,
  type ShortUrlMutationPresentationPages,
} from '~/swr-hooks/shortUrl/getShortUrlUpdatePresentationPages';
import {
  getShortUrlUpdatePresentationSettings,
  type ShortUrlMutationPresentationSettings,
} from '~/swr-hooks/shortUrl/getShortUrlUpdatePresentationSettings';
import { replacePresentationSubObjectsMergeCustomizer } from '~/swr-hooks/shortUrl/replacePresentationSubObjectsMergeCustomizer';
import { getShortIdInformationKey } from '~/swr-hooks/shortUrl/useGetExistingShortIdInformation';

type ShortUrlUpdateApiPayload = Parameters<typeof ShortUrl.update>[0];

type ShareUrlUpdatePayload = Omit<
  ShortUrlUpdateApiPayload,
  'presentationSettings' | 'presentationContent' | 'presentationPages' | 'boardContext'
> & {
  presentationSettings?: ShortUrlMutationPresentationSettings;
  presentationContent?: ShortUrlMutationPresentationContent;
  presentationPages?: ShortUrlMutationPresentationPages;
  permissions: ShortUrlResponse['permissions'];
};

type ShareUrlUpdatePayloadInput = ShareUrlUpdatePayload & {
  boardContext?: ShortUrlResponse['boardContext'];
  id?: ShortUrlResponse['id'];
};

type UpdateShortIdParams = {
  objectId: Board['id'] | Clip['id'] | Form['id'];
  boardContext?: ShortUrlResponse['boardContext'];
  updatePayload: ShareUrlUpdatePayloadInput;
  analyticsPayload?: {
    objectType: ShareUrlGeneratedObjectType;
    setting: ShareUrlUpdatedSetting;
  };
};

export type ShareUrlUpdateHandlerProps = DeepPartial<Omit<ShareUrlUpdatePayload, 'workspaceId' | 'shortId'>>;

export const useUpdateShortId = () => {
  const queryClient = useQueryClient();
  const { trackShareUrlUpdated } = useTrackShareUrlUpdated();

  const updateShortId = useMutation({
    mutationFn: ({ updatePayload }: UpdateShortIdParams) => {
      const { permissions, presentationSettings, presentationContent, presentationPages, ...rest } = updatePayload;

      return ShortUrl.update({
        ...rest,
        boardContext: rest.boardContext ?? undefined,
        ...(presentationSettings !== undefined
          ? { presentationSettings: getShortUrlUpdatePresentationSettings(presentationSettings) }
          : {}),
        ...(presentationContent !== undefined
          ? { presentationContent: getShortUrlUpdatePresentationContent(presentationContent) }
          : {}),
        ...(presentationPages !== undefined
          ? { presentationPages: getShortUrlUpdatePresentationPages(presentationPages) }
          : {}),
        permissions: {
          asset: permissions.asset,
          customField: permissions.customField,
        },
      });
    },
    onMutate: ({ objectId, boardContext, updatePayload }) => {
      const previousShortInfo = queryClient.getQueryData<ShortUrlResponse>(
        getShortIdInformationKey({ objectId, boardContext }),
      );

      if (previousShortInfo) {
        queryClient.setQueryData<ShortUrlResponse>(
          getShortIdInformationKey({ objectId, boardContext }),
          mergeWith({}, previousShortInfo, updatePayload, replacePresentationSubObjectsMergeCustomizer),
        );
      }

      return { previousShortInfo };
    },
    onSuccess: (data, { objectId, boardContext, updatePayload, analyticsPayload }) => {
      const previousShortInfo = queryClient.getQueryData<ShortUrlResponse>(
        getShortIdInformationKey({ objectId, boardContext }),
      );

      const mergedShortInfo = previousShortInfo
        ? mergeWith({}, previousShortInfo, data, replacePresentationSubObjectsMergeCustomizer)
        : data;
      const shareId = mergedShortInfo.id ?? data.id ?? updatePayload.shortId;

      if (previousShortInfo) {
        queryClient.setQueryData<ShortUrlResponse>(
          getShortIdInformationKey({ objectId, boardContext }),
          mergedShortInfo,
        );
      }

      if (analyticsPayload && shareId) {
        trackShareUrlUpdated({
          share_id: shareId,
          object_type: analyticsPayload.objectType,
          ...analyticsPayload.setting,
        });
      }
    },
    onError: (error, { objectId, boardContext, updatePayload }, context) => {
      reportErrorToBugsnag({
        error,
        context: 'Failed to update shortId ',
        metadata: { data: { objectId, boardContext, updatePayload } },
      });

      if (context?.previousShortInfo) {
        queryClient.setQueryData<ShortUrlResponse>(
          getShortIdInformationKey({ objectId, boardContext }),
          context?.previousShortInfo,
        );
      }
    },
  });

  return {
    updateShortId,
  };
};
